/**
 * Utility functions for extracting project codes from URLs
 * Supports pattern:
 * - https://s.laava.id/XXXXX
 * where XXXXX is a 5-digit alphanumeric project code
 * 
 * Note: Direct scan URLs like /t/[TAG_ID]/XXXXX are not supported
 * because direct scans don't use timeout screens
 */

/**
 * Validates if a string is a valid 5-digit alphanumeric project code
 * @param {string} code - The code to validate
 * @returns {boolean} - True if valid project code
 */
export const isValidProjectCode = (code) => {
  if (!code || typeof code !== 'string') {
    return false
  }
  
  // Must be exactly 5 characters, alphanumeric only
  return /^[A-Za-z0-9]{5}$/.test(code)
}

/**
 * Extracts project code from URL pathname
 * @param {string} pathname - The URL pathname (e.g., from window.location.pathname)
 * @returns {string|null} - The project code if found and valid, null otherwise
 */
export const extractProjectCodeFromPath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') {
    return null
  }

  // Remove leading/trailing slashes and split into segments
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/')
  
  // Pattern: /XXXXX or /XXXXX/timeout - project code is the 1st segment
  // Note: We don't extract from /t/[TAG_ID]/XXXXX patterns because direct scans don't use timeout screens
  if (segments.length >= 1) {
    const potentialCode = segments[0]
    if (isValidProjectCode(potentialCode)) {
      return potentialCode
    }
  }
  
  return null
}

/**
 * Extracts project code from URL search parameters
 * @param {string} search - The URL search string (e.g., from window.location.search)
 * @returns {string|null} - The project code if found and valid, null otherwise
 */
export const extractProjectCodeFromQuery = (search) => {
  if (!search || typeof search !== 'string') {
    return null
  }
  
  const params = new URLSearchParams(search)
  const projectCode = params.get('project') || params.get('projectCode')
  
  if (isValidProjectCode(projectCode)) {
    return projectCode
  }
  
  return null
}

/**
 * Extracts project code from current window location
 * Tries both pathname and query parameters
 * @returns {string|null} - The project code if found and valid, null otherwise
 */
export const extractProjectCodeFromCurrentUrl = () => {
  if (typeof window === 'undefined') {
    return null
  }
  
  // Try pathname first
  const pathCode = extractProjectCodeFromPath(window.location.pathname)
  if (pathCode) {
    return pathCode
  }
  
  // Fallback to query parameters
  return extractProjectCodeFromQuery(window.location.search)
}

/**
 * Extracts project code from any URL string
 * @param {string} url - The full URL to parse
 * @returns {string|null} - The project code if found and valid, null otherwise
 */
export const extractProjectCodeFromUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return null
  }
  
  try {
    const urlObj = new URL(url)
    
    // Try pathname first
    const pathCode = extractProjectCodeFromPath(urlObj.pathname)
    if (pathCode) {
      return pathCode
    }
    
    // Fallback to query parameters
    return extractProjectCodeFromQuery(urlObj.search)
  } catch (error) {
    // Invalid URL, try treating as pathname only
    return extractProjectCodeFromPath(url)
  }
}
